import { useMemo } from 'react'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import { Divider, Stack, Tooltip, Typography } from '@mui/material'
import {
  useGetJobsLiveList,
  useGetPhasesLiveList,
} from '@/services/hooks/relay/live-lists'
import { CellColumn } from '@/utils/po-discrepancies'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import LineItemsTable, {
  CellDiscrepanciesMap,
  LineItemColumnVisibility,
  LineItemsTableRow,
} from './LineItemsTable'
import {
  FieldDiscrepancy,
  FieldItem,
  getSharedLineItemJob,
  getSharedLineItemPhase,
} from './shared'

export type PurchaseOrderDataProps = {
  vendorName: string | null
  date: string | null
  lineItems: LineItemsTableRow[]
  rowRefCallback?: (rowId: string) => (el: HTMLTableRowElement | null) => void
  highlightedRowIds?: Set<string>
  showHeaderJobNumber?: boolean
  showHeaderPhaseNumber?: boolean
  /** Mirrors the invoice side's field config so both tables show the same columns. */
  columnVisibility?: LineItemColumnVisibility
  /** Marks the Vendor FieldItem with a discrepancy — set on VENDOR_MISMATCH header discrepancies. */
  vendorDiscrepancy?: FieldDiscrepancy
  /** Renders the Vendor field's discrepancy emphasized (hovered here or on the invoice's vendor field). */
  vendorDiscrepancyEmphasized?: boolean
  /** Fired when the pointer enters/leaves the Vendor field's discrepancy. */
  onVendorDiscrepancyHover?: (hovering: boolean) => void
  /** Notes in the header that this PO's number matches the invoice's — the positive counterpart to the invoice's "N lines unmatched". */
  poNumberMatch?: boolean
  /** Per-row cell discrepancies (quantity/rate/amount), keyed by row id. */
  cellDiscrepancies?: CellDiscrepanciesMap
  /** Cells with a discrepancy to render emphasized (hovered here or counterpart hovered on the invoice side). */
  emphasizedCells?: Set<string>
  onRowHover?: (rowId: string | null) => void
  onDiscrepancyCellClick?: (
    rowId: string,
    column: CellColumn,
    anchorEl: HTMLElement,
  ) => void
  onDiscrepancyCellHover?: (rowId: string, column: CellColumn | null) => void
}

export default function PurchaseOrderData({
  vendorName,
  date,
  lineItems,
  rowRefCallback,
  highlightedRowIds,
  showHeaderJobNumber,
  showHeaderPhaseNumber,
  columnVisibility,
  vendorDiscrepancy,
  vendorDiscrepancyEmphasized,
  onVendorDiscrepancyHover,
  poNumberMatch,
  cellDiscrepancies,
  emphasizedCells,
  onRowHover,
  onDiscrepancyCellClick,
  onDiscrepancyCellHover,
}: PurchaseOrderDataProps) {
  const { document } = useDocumentContext()
  const { project } = useProjectContext()

  const sharedPoJobNumber = useMemo(() => {
    if (!showHeaderJobNumber) return null
    return getSharedLineItemJob(lineItems)
  }, [lineItems, showHeaderJobNumber])
  const sharedPoPhaseNumber = useMemo(() => {
    if (!showHeaderPhaseNumber) return null
    return getSharedLineItemPhase(lineItems)
  }, [lineItems, showHeaderPhaseNumber])

  const { jobs } = useGetJobsLiveList({
    orgId: document?.owner_org_id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    filters: sharedPoJobNumber ? { num__eq: sharedPoJobNumber } : undefined,
    enabled: showHeaderJobNumber && !!sharedPoJobNumber,
  })

  const { phases } = useGetPhasesLiveList({
    orgId: document?.owner_org_id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    jobNum: lineItems[0]?.jobNumber || '',
    filters: sharedPoPhaseNumber ? { num__eq: sharedPoPhaseNumber } : undefined,
    enabled: showHeaderPhaseNumber && !!sharedPoPhaseNumber,
  })

  return (
    <>
      {/* Header fields */}
      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 2 }}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
      >
        <FieldItem
          label="Vendor"
          value={vendorName}
          discrepancy={vendorDiscrepancy}
          discrepancyEmphasized={vendorDiscrepancyEmphasized}
          onDiscrepancyHoverChange={onVendorDiscrepancyHover}
        />
        <FieldItem label="PO Date" value={date} />
        {showHeaderJobNumber && (
          <FieldItem
            label="Job"
            value={sharedPoJobNumber ?? null}
            tooltip={jobs[0]?.pd_display_name}
          />
        )}
        {showHeaderPhaseNumber && (
          <FieldItem
            label="Phase"
            value={sharedPoPhaseNumber ?? null}
            tooltip={phases[0]?.pd_display_name}
          />
        )}
        {poNumberMatch && (
          <Tooltip
            title="This PO's number matches what's on the invoice."
            enterDelay={200}
            disableInteractive
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ ml: 'auto', alignSelf: 'flex-end', flexShrink: 0 }}
            >
              <CheckCircleOutline
                sx={{ fontSize: 16, color: 'success.main' }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                color="success.main"
                noWrap
                // caption's 1.66 line-height leaves uneven space above and
                // below the glyphs, which throws off centering against the
                // icon; collapsing it makes the text box hug the text.
                sx={{ lineHeight: 1 }}
              >
                Matching PO Number
              </Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>

      <Divider />

      <LineItemsTable
        rows={lineItems}
        rowRefCallback={rowRefCallback}
        highlightedRowIds={highlightedRowIds}
        columnVisibility={columnVisibility}
        cellDiscrepancies={cellDiscrepancies}
        emphasizedCells={emphasizedCells}
        onRowHover={onRowHover}
        onDiscrepancyCellClick={onDiscrepancyCellClick}
        onDiscrepancyCellHover={onDiscrepancyCellHover}
      />
    </>
  )
}
